import { Col, DatePicker, Row, Segmented, Grid, Tooltip } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import locale from 'antd/es/date-picker/locale/az_AZ'
import moment, { Moment } from 'moment'
import { SegmentedLabeledOption } from 'rc-segmented'
import styles from './Segmented.module.scss'
import { ErrorNotificationIco } from '@modules/CustomIcons'
import { useFetchBusinessDateMutation } from '@app/store/reducers/configs/businessDate.api'

type DateFilterOption = Omit<SegmentedLabeledOption, 'value'> & {
  value: 'yesterday' | 'today' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'null'
  dateFrom: Moment
  dateTo: Moment
}

interface FilterDateInputsProps {
  value?: 'today' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'null'
  startDateError?: boolean
  startDateErrorMessage?: string
  endDateError?: boolean
  endDateErrorMessage?: string
  onChangePicker: (value: {
    dateFrom?: Moment | null
    dateTo?: Moment | null
  }) => void
}

function FilterDateInputs(props: FilterDateInputsProps) {
  const {
    value,
    onChangePicker,
    startDateError,
    endDateError,
    endDateErrorMessage,
    startDateErrorMessage,
  } = props
  const { xl } = Grid.useBreakpoint()
  const { t } = useTranslation()
  const dateFormat = 'DD.MM.YYYY'
  const lang = localStorage.getItem('i18nextLng');

  const [isDiffSameOrAfter, setDiff] = useState(true)
  const [startDate, setStartDate] = useState<Moment | null>(
    moment(moment().format(dateFormat), dateFormat)
  )
  const [endDate, setEndDate] = useState<Moment>(moment())
  // const [businessDate, setBusinessDate] = useState<Moment>(moment())
  const [activeIndexDate, setDateIndex] = useState(value)

  const [_, { data: businessDate, isLoading, isSuccess }] =
    useFetchBusinessDateMutation({
      fixedCacheKey: 'businessDate',
    })

  const options: DateFilterOption[] = useMemo(
    () => [
      {
        label: t('transfers.today'),
        value: 'today',
        dateFrom: moment(moment(), dateFormat),
        dateTo: moment(businessDate, dateFormat),
      },
      // {
      //   label: t('transfers.yesterday'),
      //   value: 'yesterday',
      //   dateFrom: moment().add(-1, 'days'),
      //   dateTo: moment().add(-1, 'days'),
      // },
      {
        label: t('transfers.thisWeek'),
        value: 'thisWeek',
        dateFrom: moment(moment().startOf('week').add(1, 'days'), dateFormat),
        dateTo: moment(businessDate, dateFormat),
      },
      {
        label: t('transfers.thisMonth'),
        value: 'thisMonth',
        dateFrom: moment(moment().startOf('month'), dateFormat),
        dateTo: moment(businessDate, dateFormat),
      },
      {
        label: t('accountStatement.thisYear'),
        value: 'thisYear',
        dateFrom: moment().startOf('year'),
        dateTo: moment(businessDate, dateFormat),
      },
    ],
    [isLoading, lang]
  )

  const onSegmentedChange = (value: DateFilterOption['value']) => {
    setDateIndex(value)

    const dates = options.find((o) => o.value === value)
    setStartDate(moment(dates?.dateFrom))
    setEndDate(moment(dates?.dateTo))

    onChangePicker({ dateFrom: dates?.dateFrom, dateTo: dates?.dateTo })
  }

  const handleStartDate = (value: Moment) => {
    if (value === null) {
      setStartDate(null)
      setDateIndex('null')
      onChangePicker({ dateFrom: null, dateTo: endDate })
    } else {
      setDateIndex('null')
      setStartDate(moment(value, dateFormat))
      onChangePicker({ dateFrom: value, dateTo: endDate })
    }
  }

  const handleEndDate = (value: Moment) => {
    if (value === null) {
      setEndDate(null)
      setDateIndex('null')
      onChangePicker({ dateTo: null, dateFrom: startDate })
    } else {
      setDateIndex('null')
      setEndDate(moment(value, dateFormat))
      onChangePicker({ dateTo: value, dateFrom: startDate })
    }
  }

  useEffect(() => {
    if (isSuccess) {
      const dates = options.find((o) => o.value === activeIndexDate)
      setStartDate(moment(dates?.dateFrom, dateFormat))
      setEndDate(moment(dates?.dateTo, dateFormat))
      onChangePicker({ dateTo: dates?.dateTo, dateFrom: dates?.dateFrom })
    }
  }, [isSuccess])

  return (
    <Row
      className={styles.datepicker}
      justify="start"
      align={xl ? 'middle' : 'stretch'}
      style={{ gap: 16, flexDirection: xl ? 'row' : 'column' }}
    >
      <Col>
        <Row justify="start" align="middle">
          <div style={{ marginRight: '10px' }}>{t('transfers.period')}</div>
          <div>
            <DatePicker
              name="startDate"
              locale={locale}
              status={startDateError ? 'error' : ''}
              className={!isDiffSameOrAfter ? 'errored-ant-picker' : ''}
              onChange={handleStartDate}
              value={startDate ? moment(startDate, dateFormat) : undefined}
              format={dateFormat}
              allowClear={false}
            />
            {startDateError && (
              <Tooltip
                style={{ position: 'absolute', right: -50, cursor: 'pointer' }}
                color="#f3646c"
                title={startDateErrorMessage ? t(startDateErrorMessage) : ''}
              >
                <div className={styles.errorTooltipIcon}>
                  <ErrorNotificationIco />
                </div>
              </Tooltip>
            )}
          </div>
          <span style={{ margin: 'auto 10px' }}>-</span>
          <div style={{ position: 'relative' }}>
            <DatePicker
              name="endDate"
              status={endDateError ? 'error' : ''}
              locale={locale}
              className={!isDiffSameOrAfter ? 'errored-ant-picker' : ''}
              onChange={handleEndDate}
              value={endDate ? moment(endDate, dateFormat) : undefined}
              format={dateFormat}
              allowClear={false}
            />
            {endDateError && (
              <Tooltip
                style={{ position: 'absolute', right: -50, cursor: 'pointer' }}
                color="#f3646c"
                title={endDateErrorMessage ? t(endDateErrorMessage) : ''}
              >
                <div className={styles.errorTooltipIcon}>
                  <ErrorNotificationIco />
                </div>
              </Tooltip>
            )}
          </div>
        </Row>
      </Col>
      <Col>
        <Segmented
          className={styles.customSegmented}
          options={options}
          value={activeIndexDate}
          onChange={onSegmentedChange}
        />
      </Col>
    </Row>
  )
}

export default FilterDateInputs
